/**
 * Where a person checks their own account on a channel: the profile when the
 * stored name is a handle the channel's URLs use, else the channel's home,
 * where they are signed in. Used when a post may already be live
 * (PUBLISH_OUTCOME_UNKNOWN): "check before publishing again".
 */

const HOME: Readonly<Record<string, string>> = {
  INSTAGRAM: 'https://www.instagram.com/',
  FACEBOOK: 'https://www.facebook.com/',
  TWITTER: 'https://x.com/',
  LINKEDIN: 'https://www.linkedin.com/feed/',
  YOUTUBE: 'https://studio.youtube.com/',
  PINTEREST: 'https://www.pinterest.com/',
  TIKTOK: 'https://www.tiktok.com/',
  TELEGRAM: 'https://web.telegram.org/',
  WHATSAPP: 'https://business.facebook.com/wa/manage/',
  GOOGLE_BUSINESS: 'https://business.google.com/',
  ZETPIN: 'https://zetpin.com/',
};

/** Channels whose stored name (platformUsername) is the handle in their profile URL. */
const HANDLE_PROFILE: Readonly<Record<string, (handle: string) => string>> = {
  INSTAGRAM: (h) => `https://www.instagram.com/${h}/`,
  TWITTER: (h) => `https://x.com/${h}`,
  TIKTOK: (h) => `https://www.tiktok.com/@${h}`,
};

/**
 * A Facebook Page name or an Instagram account stored by its Page's title
 * ("Spice Garden") is not a handle; only these characters make one.
 */
const HANDLE = /^[A-Za-z0-9._]{1,30}$/;

/** The profile (or home) URL of a channel; `platform` as the API names it (INSTAGRAM, GOOGLE_BUSINESS). */
export function platformProfileUrl(platform: string, username?: string | null): string | null {
  const key = platform.toUpperCase();
  const handle = username?.trim().replace(/^@/, '') ?? '';
  const profile = HANDLE_PROFILE[key];
  if (profile && HANDLE.test(handle)) return profile(handle);
  return HOME[key] ?? null;
}

/** Where a post can be opened, from what the channel returned when it was published. */
export interface PostUrlContext {
  /** The post type it went out as (YouTube Short, Facebook Reel or video). */
  postType?: string | null;
  /** TELEGRAM: the chat it was sent to (`@channel` or a numeric id). */
  chatId?: string | null;
}

/**
 * The public link of a published post when the channel returned only its id
 * (Task 1.3, receipts), by each channel's documented URL pattern. Null when
 * the channel has no public link for it: a private Telegram group, an X post
 * whose id is missing, WhatsApp. Instagram, TikTok and Google Business
 * return their own link, which is kept as it is.
 */
export function platformPostUrl(platform: string, postId: string | null | undefined, ctx: PostUrlContext = {}): string | null {
  const id = postId?.trim();
  if (!id) return null;
  const type = (ctx.postType ?? '').toLowerCase();
  switch (platform.toUpperCase()) {
    case 'TWITTER':
      return `https://x.com/i/web/status/${encodeURIComponent(id)}`;
    case 'FACEBOOK':
      // A video or Reel id opens on its own page; a post (pageId_postId) or photo id on facebook.com/{id}.
      if (type === 'reel') return `https://www.facebook.com/reel/${encodeURIComponent(id)}`;
      if (type === 'video') return `https://www.facebook.com/watch/?v=${encodeURIComponent(id)}`;
      return `https://www.facebook.com/${encodeURIComponent(id)}`;
    case 'LINKEDIN':
      return id.startsWith('urn:li:') ? `https://www.linkedin.com/feed/update/${id}/` : null;
    case 'YOUTUBE':
      return type === 'short' ? `https://www.youtube.com/shorts/${encodeURIComponent(id)}` : `https://www.youtube.com/watch?v=${encodeURIComponent(id)}`;
    case 'PINTEREST':
      return `https://www.pinterest.com/pin/${encodeURIComponent(id)}/`;
    case 'TELEGRAM': {
      const chat = ctx.chatId?.trim() ?? '';
      if (!/^\d+$/.test(id)) return null;
      // A public channel by its @name; a private supergroup (-100…) by t.me/c, which only its members can open.
      if (/^@[A-Za-z0-9_]{5,32}$/.test(chat)) return `https://t.me/${chat.slice(1)}/${id}`;
      const m = /^-100(\d+)$/.exec(chat);
      return m ? `https://t.me/c/${m[1]}/${id}` : null;
    }
    default:
      return null;
  }
}
